import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { ColDef, ColGroupDef, ICellRendererParams } from "ag-grid-enterprise";
import {
  LinkCellRenderer,
  ManualIconCellRenderer,
} from "@/controls/ag-grid/components/ButtonCellRenderer";
import { CommentsColumn } from "@/controls/ag-grid/components/CommentsColumn";
import { CheckboxRenderer } from "@/controls/ag-grid/components/CheckboxRenderer";

type Props = {
  showCommentModal: boolean;
  setShowCommentModal: (
    data: any,
    position: { top: number; left: number },
  ) => void;
};

export const useCasterColumns = ({ setShowCommentModal }: Props) => {
  const columns: (ColDef | ColGroupDef)[] = [
    {
      field: "HeatName",
      headerName: "Heat",
      cellRenderer: (params: ICellRendererParams) => {
        return LinkCellRenderer({
          ...params,
          link: `/meltshop-heats/${params.data.HeatShiftId}?heatName=${params.data.HeatName}`,
        });
      },
      flex: 1,
      minWidth: 60,
    },
    {
      field: "Date",
      headerName: "Date",
      flex: 1,
      minWidth: 70,
    },
    {
      field: "Shift",
      headerName: "Shift",
      flex: 1,
      minWidth: 40,
    },
    {
      field: "GradeName",
      headerName: "Grade",
      flex: 1,
      minWidth: 90,
    },
    {
      field: "LadleName",
      headerName: "Ladle",
      flex: 1,
      minWidth: 40,
    },
    {
      field: "Crew",
      headerName: "Crew",
      flex: 1,
      minWidth: 40,
    },
    {
      headerName: "Times",
      children: [
        {
          field: "Start",
          headerName: "Arrive",
          valueGetter: (params) =>
            dxtToLocalServerTime(params.data?.Start, "HH:mm:ss") || "",
          flex: 1,
          minWidth: 60,
        },
        {
          field: "LadleOpen",
          headerName: "Open",
          cellRenderer: (params: ICellRendererParams) => {
            return LinkCellRenderer({
              ...params,
              link: `/caster-data-entry/${params.data.ProcessId}`,
            });
          },
          valueGetter: (params) =>
            dxtToLocalServerTime(params.data?.LadleOpen, "HH:mm:ss") || "",
          flex: 1,
          minWidth: 60,
        },
        {
          field: "LadleClose",
          headerName: "Close",
          valueGetter: (params) =>
            dxtToLocalServerTime(params.data?.LadleClose, "HH:mm:ss") || "",
          flex: 1,
          minWidth: 60,
        },
        {
          field: "End",
          headerName: "End",
          valueGetter: (params) =>
            dxtToLocalServerTime(params.data?.End, "HH:mm:ss") || "",
          flex: 1,
          minWidth: 60,
        },
        {
          field: "Duration",
          headerName: "Dur",
          valueGetter: (params) => params.data?.Duration?.toFixed(1) || "",
          flex: 1,
          minWidth: 40,
        },
      ],
    } as ColGroupDef,
    {
      field: "MoldSize",
      headerName: "Size",
      flex: 1,
      minWidth: 60,
    },
    {
      field: "CastTons",
      headerName: "Tons",
      type: ["numericColumn", "numericWidth"],
      valueGetter: (params) => params.data?.CastTons || "0",
      flex: 1,
      minWidth: 65,
    },
    {
      headerName: "Temperatures",
      children: [
        {
          field: "LastLMFTemperature",
          headerName: "LMF",
          type: ["numericColumn", "numericWidth"],
          valueGetter: (params) =>
            params.data?.LastLMFTemperature?.toFixed(0) || "",
          flex: 1,
          minWidth: 50,
        },
        {
          field: "FirstTemperature",
          headerName: "First",
          type: ["numericColumn", "numericWidth"],
          valueGetter: (params) =>
            params.data?.FirstTemperature?.toFixed(0) || "",
          flex: 1,
          minWidth: 50,
        },
        {
          field: "LastTemperature",
          headerName: "Last",
          type: ["numericColumn", "numericWidth"],
          valueGetter: (params) =>
            params.data?.LastTemperature?.toFixed(0) || "",
          flex: 1,
          minWidth: 50,
        },
        {
          field: "LastSuperHeat",
          headerName: "Last SH",
          headerTooltip: "Last Super Heat",
          type: ["numericColumn", "numericWidth"],
          valueGetter: (params) => params.data?.LastSuperHeat?.toFixed(0) || "",
          flex: 1,
          minWidth: 50,
        },
      ],
    } as ColGroupDef,
    {
      field: "Length",
      headerName: "Length",
      flex: 1,
      minWidth: 60,
    },
    {
      headerName: "Tundish",
      children: [
        {
          field: "TundishName",
          headerName: "Nr.",
          flex: 1,
          minWidth: 40,
        },
        {
          field: "HeatsOnTundish",
          headerName: "#",
          headerTooltip: "Heats On Tundish",
          flex: 1,
          minWidth: 40,
        },
      ],
    },
    {
      headerName: "Strand 1",
      children: [
        {
          field: "MoldS1",
          headerName: "Mold",
          headerTooltip: "Mold Name",
          flex: 1,
          minWidth: 40,
        },
        {
          field: "SpeedS1",
          headerName: "SPD",
          headerTooltip: "Speed",
          flex: 1,
          minWidth: 40,
        },
        {
          field: "TotalCutsS1",
          headerName: "#",
          headerTooltip: "Total Cuts",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 40,
        },
      ],
    } as ColGroupDef,
    {
      headerName: "Strand 2",
      children: [
        {
          field: "MoldS2",
          headerName: "Mold",
          headerTooltip: "Mold Name",
          flex: 1,
          minWidth: 40,
        },
        {
          field: "SpeedS2",
          headerName: "SPD",
          headerTooltip: "Speed",
          flex: 1,
          minWidth: 40,
        },
        {
          field: "TotalCutsS2",
          headerName: "#",
          headerTooltip: "Total Cuts",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 40,
        },
      ],
    } as ColGroupDef,
    {
      headerName: "Strand 3",
      children: [
        {
          field: "MoldS3",
          headerName: "Mold",
          headerTooltip: "Mold Name",
          flex: 1,
          minWidth: 40,
        },
        {
          field: "SpeedS3",
          headerName: "SPD",
          headerTooltip: "Speed",
          flex: 1,
          minWidth: 40,
        },
        {
          field: "TotalCutsS3",
          headerName: "#",
          headerTooltip: "Total Cuts",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 40,
        },
      ],
    } as ColGroupDef,
    {
      headerName: "Strand 4",
      children: [
        {
          field: "MoldS4",
          headerName: "Mold",
          headerTooltip: "Mold Name",
          flex: 1,
          minWidth: 40,
        },
        {
          field: "SpeedS4",
          headerName: "SPD",
          headerTooltip: "Speed",
          flex: 1,
          minWidth: 40,
        },
        {
          field: "TotalCutsS4",
          headerName: "#",
          headerTooltip: "Total Cuts",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 40,
        },
      ],
    } as ColGroupDef,
    {
      headerName: "Strand 5",
      children: [
        {
          field: "MoldS5",
          headerName: "Mold",
          headerTooltip: "Mold Name",
          flex: 1,
          minWidth: 40,
        },
        {
          field: "SpeedS5",
          headerName: "SPD",
          headerTooltip: "Speed",
          flex: 1,
          minWidth: 40,
        },
        {
          field: "TotalCutsS5",
          headerName: "#",
          headerTooltip: "Total Cuts",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 40,
        },
      ],
    } as ColGroupDef,
    {
      field: "IsManualOpen",
      headerName: "Manual Opened",
      flex: 1,
      minWidth: 60,
      cellRenderer: CheckboxRenderer,
      valueGetter: (params) => (params.data?.IsManualOpen ? "Yes" : ""),
      type: ["centerAligned"],
    },
    {
      field: "IsLadleShrouded",
      headerName: "Ladle Shrouded",
      flex: 1,
      minWidth: 60,
      cellRenderer: CheckboxRenderer,
      valueGetter: (params) => (params.data?.IsLadleShrouded ? "Yes" : ""),
      type: ["centerAligned"],
    },
    {
      field: "Comments",
      headerName: "Notes",
      flex: 1,
      minWidth: 50,
      cellRenderer: (params: ICellRendererParams) => {
        return CommentsColumn({
          data: params?.data,
          setShowOpenModal: (
            show: boolean,
            data: any,
            position: { top: number; left: number },
          ) => {
            if (show) {
              setShowCommentModal(data, position);
            }
          },
        });
      },
      valueGetter: (params) => params.data?.Comments || "",
    },
    {
      field: "IsManual",
      headerName: "",
      type: ["nonSorteable", "nonFilterable", "centerAligned"],
      cellRenderer: ManualIconCellRenderer,
      cellClass: "ag-button-cell",
      flex: 1,
      minWidth: 50,
    },
  ];

  return { columns };
};
